/** MetricValue.source for hand-entered rows. */
export const METRIC_VALUE_SOURCE_MANUAL = 'manual';

/** MetricValue.source / Metric.source for canonical library metrics and provider→canonical mirrors. */
export const METRIC_VALUE_SOURCE_BASE = 'base';

/** MetricValue.source for HealthKit sync rows. */
export const METRIC_VALUE_SOURCE_HEALTHKIT = 'healthkit';

/** MetricValue.source for Android Health Connect sync rows. */
export const METRIC_VALUE_SOURCE_HEALTH_CONNECT = 'health-connect';

/** Metric.source / MetricValue.source for formula-computed rows. */
export const METRIC_VALUE_SOURCE_CALCULATED = 'calculated';

/** Metric.source / MetricValue.source for Oura sync rows. */
export const METRIC_VALUE_SOURCE_OURA = 'oura';

/** Metric.source / MetricValue.source for Fitbit sync rows. */
export const METRIC_VALUE_SOURCE_FITBIT = 'fitbit';

/** Metric.source / MetricValue.source for habit-compliance rows. */
export const METRIC_VALUE_SOURCE_COMPLIANCE = 'compliance';

/** Editor source-kind toggle. `synced` is virtual — persist the provider id instead. */
export type MetricSourceKind = 'manual' | 'calculated' | 'base' | 'synced' | 'compliance';

/** Source values that are kinds, not sync providers. */
export const INTRINSIC_METRIC_SOURCES: readonly MetricSourceKind[] = [
  METRIC_VALUE_SOURCE_MANUAL,
  METRIC_VALUE_SOURCE_CALCULATED,
  METRIC_VALUE_SOURCE_BASE,
  METRIC_VALUE_SOURCE_COMPLIANCE,
];

/** Known sync provider ids shown in the Source picker. */
export const KNOWN_SYNCED_METRIC_SOURCES: readonly string[] = [
  METRIC_VALUE_SOURCE_HEALTHKIT,
  METRIC_VALUE_SOURCE_HEALTH_CONNECT,
  METRIC_VALUE_SOURCE_OURA,
  METRIC_VALUE_SOURCE_FITBIT,
];

/**
 * Returns whether `source` is an intrinsic kind (`manual` / `calculated` / `base`).
 * @param source Metric.source value.
 * @returns True when the value is not a sync provider.
 */
export function isIntrinsicMetricSource(source: string): boolean {
  return (INTRINSIC_METRIC_SOURCES as readonly string[]).includes(source);
}

/**
 * Returns whether `source` is a persistable sync provider id.
 * @param source Metric.source value.
 * @returns True for non-empty provider ids (not a kind, not `synced`).
 */
export function isSyncedMetricSource(source: string): boolean {
  return source.length > 0 && source !== 'synced' && !isIntrinsicMetricSource(source);
}

/**
 * Maps a persisted Metric.source to the editor source-kind toggle.
 * @param source Metric.source value.
 * @returns `synced` for any provider id.
 */
export function sourceKindFromSource(source: string): MetricSourceKind {
  if (source === METRIC_VALUE_SOURCE_CALCULATED) {
    return 'calculated';
  }
  if (source === METRIC_VALUE_SOURCE_MANUAL) {
    return 'manual';
  }
  if (source === METRIC_VALUE_SOURCE_BASE) {
    return 'base';
  }
  if (source === METRIC_VALUE_SOURCE_COMPLIANCE) {
    return 'compliance';
  }
  return 'synced';
}

/**
 * Maps the editor source-kind toggle (and optional provider) to Metric.source.
 * @param sourceKind Editor toggle value.
 * @param syncedSource Provider id when kind is `synced`.
 * @returns Persistable source string; empty when synced but no valid provider.
 */
export function sourceFromSourceKind(
  sourceKind: MetricSourceKind,
  syncedSource: string | null | undefined,
): string {
  if (sourceKind !== 'synced') {
    return sourceKind;
  }
  const trimmed = (syncedSource ?? '').trim();
  return isSyncedMetricSource(trimmed) ? trimmed : '';
}
